import type {
  AnimalDocumentDto,
  AnimalDocumentDtoType,
  AnimalHealthOperationDto,
} from '../../interactors/gen/backendClient'
import type { ContentAccess, MemberAccessPolicy } from '../../utils/hooks/useMemberAccessPolicy'

export type ContentLimitsOfferStage = 'trial' | 'ended'

export interface AnimalContentQuota {
  max: number
  used: number
  remaining: number
}

/**
 * Documents the adoption workflow attaches to the animal by itself: the backend neither counts nor
 * refuses them.
 */
const SYSTEM_GENERATED_DOCUMENT_TYPES: AnimalDocumentDtoType[] = [
  'signed-adoption-certificate',
  'signed-adoption-contract',
]

export const countAnimalDocumentsAgainstQuota = (documents?: AnimalDocumentDto[]): number =>
  (documents ?? []).filter((document) => !SYSTEM_GENERATED_DOCUMENT_TYPES.includes(document.type)).length

/** Only `health.operations` count as health follow-up entries; vaccinations, pathologies and tests do not. */
export const countAnimalHealthEntries = (operations?: AnimalHealthOperationDto[]): number => operations?.length ?? 0

/**
 * The per-animal limits the organization is actually subject to, or null when it is not limited
 * (or when the backend does not send `content` yet).
 */
export const getLimitedContentAccess = (policy: MemberAccessPolicy | null): ContentAccess | null => {
  const content = policy?.content
  if (content == null || content.isUnlimited) {
    return null
  }

  return content
}

const toQuota = (max: number | null | undefined, used: number): AnimalContentQuota | null => {
  if (max == null) {
    return null
  }

  return { max, used, remaining: Math.max(max - used, 0) }
}

export const getAnimalDocumentsQuota = (
  content: ContentAccess | null,
  documents?: AnimalDocumentDto[]
): AnimalContentQuota | null => toQuota(content?.maxDocumentsPerAnimal, countAnimalDocumentsAgainstQuota(documents))

export const getAnimalHealthEntriesQuota = (
  content: ContentAccess | null,
  operations?: AnimalHealthOperationDto[]
): AnimalContentQuota | null => toQuota(content?.maxHealthEntriesPerAnimal, countAnimalHealthEntries(operations))

/**
 * Which content limits message to show the organization, if any.
 *
 * Only a free organization is ever concerned: Essential and Premium have nothing to configure or buy.
 * A missing `plan` or `content` (backend not deployed yet) means no message at all.
 * - `trial`: the three months offered at creation are running; the limits apply once they end.
 * - `ended`: the limits apply now.
 * Any other unlimited reason (grandfathered, Stripe Connect…) leaves nothing to sell.
 */
export const getContentLimitsOfferStage = (policy: MemberAccessPolicy | null): ContentLimitsOfferStage | null => {
  if (policy?.plan !== 'free' || policy.content == null) {
    return null
  }

  if (!policy.content.isUnlimited) {
    return 'ended'
  }

  return policy.content.unlimitedReason === 'free-trial' ? 'trial' : null
}
